// CT Predictive Search - Custom Element for CoinTown
// Copy to: Shopify Admin > Themes > Edit Code > assets/ct-predictive-search.js

class CTPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('[role="status"]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.searchTerm = searchTerm;
    this.getSearchResults(searchTerm);
  }

  onFocus() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) return;
    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.isOpen) return;
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    if (event.code === 'Escape') {
      this.close(true);
    }
  }

  switchOption(direction) {
    const options = Array.from(this.querySelectorAll('[role="option"]:not([aria-disabled="true"])')).filter(el => el.offsetParent !== null);
    if (!options.length) return;
    const selectedOption = this.querySelector('[aria-selected="true"]');
    let selectedIndex = options.indexOf(selectedOption);
    if (direction === 'down') {
      selectedIndex = selectedIndex === -1 ? 0 : (selectedIndex + 1) % options.length;
    } else {
      selectedIndex = selectedIndex === -1 ? options.length - 1 : (selectedIndex - 1 + options.length) % options.length;
    }
    if (selectedOption) selectedOption.setAttribute('aria-selected', 'false');
    options[selectedIndex].setAttribute('aria-selected', 'true');
    options[selectedIndex].scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', options[selectedIndex].id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"]');
    if (selectedOption) {
      const link = selectedOption.querySelector('a');
      if (link) link.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    this.abortController.abort();
    this.abortController = new AbortController();
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=ct-predictive-search&resources[type]=product,collection,query&resources[limit]=10&resources[options][unavailable_products]=hide`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-ct-predictive-search')?.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code !== 20) this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', '');
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = this.getAttribute('data-loading-text');
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    const resultsCount = this.querySelectorAll('[role="option"]').length;
    this.statusElement.textContent = `${resultsCount} results`;
    setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
  }

  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.predictiveSearchResults.style.maxHeight = this.getResultsMaxHeight() + 'px';
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  getResultsMaxHeight() {
    const inputRect = this.input.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - inputRect.bottom - 20;
    return Math.min(spaceBelow, 400);
  }
}

customElements.define('ct-predictive-search', CTPredictiveSearch);
